/**
 * Pre-publish scrub for a FROZEN WayAI slice (gh #2960).
 *
 * `wayai admin template freeze` commits a snapshot of a live hub as the template's
 * authoring source. Before those bytes ever land in git we run the same denylist scrub
 * the backend applies at push time — so the committed slice is id-free (portability,
 * gh #2939) and carries none of the author's real outbound contacts / account-scoped
 * channel identifiers (PII, gh #2946).
 *
 * The backend `sanitizeWayaiSliceFiles` stays the SINGLE authority for the *published*
 * artifact — push re-runs it idempotently, so a raw slice still publishes clean. This
 * CLI copy exists only to keep the *committed* bytes clean, and it shares the
 * load-bearing pieces (`TEMPLATE_INSTANCE_ID_KEYS` / `TEMPLATE_AUTHOR_DATA_KEYS` via the
 * core drop functions, the opaque-blob protection, and `isSanitizableSliceFile`) from
 * `@wayai/core/contracts`. A new id/PII key added there is therefore honored on both
 * sides automatically — the only thing local here is the YAML parse → drop → re-emit
 * harness, because `@wayai/core` is intentionally YAML-free.
 */

import * as yaml from 'js-yaml';
import {
  excludeTemplateAuthorData,
  isSanitizableSliceFile,
  stripTemplateInstanceIds,
} from '@wayai/core/contracts';
import { YAML_DUMP_OPTIONS } from './yaml-writer.js';

/** Strip instance ids + exclude author PII from every hub-config YAML file in a slice. */
export function sanitizeWayaiSliceFiles(files: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [filePath, body] of Object.entries(files)) {
    out[filePath] = isSanitizableSliceFile(filePath) ? sanitizeOne(body) : body;
  }
  return out;
}

function sanitizeOne(body: string): string {
  let parsed: unknown;
  try {
    parsed = yaml.load(body);
  } catch {
    return body; // not valid YAML — leave as-is (author's to fix)
  }
  const scrubbed = excludeTemplateAuthorData(stripTemplateInstanceIds(parsed));
  // Nothing removed → preserve the original bytes (formatting + comments).
  if (JSON.stringify(parsed) === JSON.stringify(scrubbed)) return body;
  return yaml.dump(scrubbed, YAML_DUMP_OPTIONS);
}
